import { gql } from '@apollo/client'
import { snakeCase, startCase } from 'lodash'
import useQuery from 'hooks/useQuery'
import usePermissions, { permissions } from 'hooks/usePermissions'

export const LicenseTransaction = {
  fragments: {
    details: gql`
      fragment LicenseTransaction_details on LicenseTransaction {
        id
        available
        category
        used
        totalLicenses

        affiliatedOrganization {
          name
          id
        }
        organization {
          id
          name
        }
        solution {
          id
          type
          usageAllowance
        }
      }
    `
  }
}

export const License = {
  fragments: {
    details: gql`
      fragment License_details on License {
        id
        licenseTransaction {
          ...LicenseTransaction_details
        }
        account {
          id
          role {
            name
          }
        }
      }

      ${LicenseTransaction.fragments.details}
    `
  }
}

export const LICENSE_TRANSACTIONS_QUERY = gql`
  query LicenseTransactionsQuery {
    licenseTransactions {
      ...LicenseTransaction_details
    }
  }

  ${LicenseTransaction.fragments.details}
`

export const ENQUIRE_SOLUTION_MUTATION = gql`
  mutation EnquireSolutionMutation($input: EnquireSolutionInput!) {
    enquireSolution(input: $input) {
      id
    }
  }
`

export const REQUEST_LICENSE_MUTATION = gql`
  mutation RequestLicenseMutation($input: RequestLicenseInput!) {
    requestLicense(input: $input) {
      id
    }
  }
`

export const LICENSE_TRANSACTION_CATEGORIES_QUERY = gql`
  query LicenseTransactionCategoriesQuery {
    licenseTransactionCategories {
      categories
    }
  }
`

export const CREATE_LICENSE_MUTATION = gql`
  mutation createLicenseMutation($input: CreateLicenseInput!) {
    createLicense(input: $input) {
      ...License_details
    }
  }

  ${License.fragments.details}
`

export const EXPIRE_LICENSE_MUTATION = gql`
  mutation ExpireLicenseMutation($id: ID!) {
    expireLicense(id: $id) {
      id
    }
  }
`

const sortSolutionsByType = solutionsList => {
  const solutionsSortOrder = [
    'OnCall',
    'OnCallCalendar',
    'Enterprise',
    'OpNote',
    'SurgeryWorkflow',
    'IntegrationHub',
    'Voice'
  ]

  return [...solutionsList].sort(
    (a, b) =>
      solutionsSortOrder.indexOf(a?.type) - solutionsSortOrder.indexOf(b?.type)
  )
}

const sortLicenseByCategory = licenseCategory => {
  const sortOrder = ['provider', 'staff', 'facility']

  licenseCategory.sort(
    (a, b) => sortOrder.indexOf(a.category) - sortOrder.indexOf(b.category)
  )

  return licenseCategory
}

const formatLicenseTransactionBySolution = licenseTypeData => {
  const sortedData = sortLicenseByCategory(licenseTypeData)
  return sortedData.map(item => ({
    id: item.id,
    category: item.category,
    used: item.used,
    available: item.available,
    total: item.totalLicenses,
    ...(item.solution.type === 'AINote'
      ? { usageAllowance: item.solution.usageAllowance }
      : {}),
    affiliatedOrganization: item.affiliatedOrganization,
    clinicOrganization: item?.organization
  }))
}

export const planOptions = ['1000', '5000', '10000', '15000']

export const formatSolutionsListforAINote = (sortedSolutions, licenses) => {
  const aiNoteSolutions = sortedSolutions.filter(
    solution => solution.title === 'AI Note'
  )

  const aiNoteLicense = licenses?.find(
    license =>
      license.licenseTransaction?.solution?.type === 'AINote' ||
      license.solution?.type === 'AINote'
  )

  const combinedDataArray = aiNoteSolutions.flatMap(obj => obj.data)

  const existingPlans = combinedDataArray.map(data => data.usageAllowance)
  planOptions.forEach(plan => {
    if (!existingPlans.includes(plan)) {
      combinedDataArray.push({
        available: 0,
        category: 'provider',
        id: `${combinedDataArray.length + 1}`,
        usageAllowance: plan,
        total: 0,
        used: 0
      })
    }
  })

  const combinedAINoteObject = {
    solutionId: !licenses?.length
      ? '8'
      : aiNoteLicense?.licenseTransaction?.solution?.id ||
        aiNoteLicense?.solution?.id,
    icon: 'ai_note',
    title: 'AI Note',
    data: aiNoteLicense ? sortLicenseByCategory(combinedDataArray) : []
  }

  const combinedSolutions = [
    ...sortedSolutions.filter(obj => obj.title !== 'AI Note'),
    combinedAINoteObject
  ]

  return combinedSolutions
}

const formatSolutionsList = ({
  sortedSolutions,
  licensesOrTransactions,
  isUserForm,
  isDynamicLicensing
}) => {
  // Formatting for AI Note
  const aiNoteLicense = licensesOrTransactions?.find(
    license =>
      license.licenseTransaction?.solution?.type === 'AINote' ||
      license.solution?.type === 'AINote'
  )

  const aiNoteSolutions = sortedSolutions.filter(
    solution => solution.title === 'AI Note'
  )

  const combinedDataArray = aiNoteSolutions.flatMap(obj => obj.data)

  const existingPlans = combinedDataArray.map(data => data.usageAllowance)

  planOptions.forEach(plan => {
    if (!existingPlans.includes(plan)) {
      combinedDataArray.push({
        available: 0,
        category: 'provider',
        id: null,
        usageAllowance: plan,
        total: 0,
        used: 0
      })
    }
  })

  const sortedProviderSolutionsByPlan = combinedDataArray.sort(
    (a, b) => Number(a.usageAllowance) - Number(b.usageAllowance)
  )

  const combinedAINoteObject = {
    solutionId: aiNoteLicense
      ? aiNoteLicense?.licenseTransaction?.solution?.id ||
        aiNoteLicense?.solution?.id
      : aiNoteSolutions[0]?.solutionId,
    icon: 'ai_note',
    title: 'AI Note',
    // eslint-disable-next-line no-nested-ternary
    data: isUserForm
      ? sortLicenseByCategory(sortedProviderSolutionsByPlan) || []
      : aiNoteLicense
        ? sortLicenseByCategory(sortedProviderSolutionsByPlan)
        : []
  }

  // Formatting for Op Note
  const opNoteSolution = sortedSolutions.find(obj => obj.title === 'Op Note')

  const opNoteLicenses = opNoteSolution?.data || []

  const groupedOpNotes = {}

  opNoteLicenses.forEach(license => {
    const key =
      license.affiliatedOrganization?.id ??
      license.licenseTransaction?.affiliatedOrganization?.id ??
      'clinicOrg'

    if (!groupedOpNotes[key]) {
      groupedOpNotes[key] = {
        affiliation:
          license.affiliatedOrganization ??
          license.licenseTransaction?.affiliatedOrganization ??
          null,
        clinicOrganization: license.clinicOrganization,
        licenses: []
      }
    }

    groupedOpNotes[key].licenses.push(license)
  })

  const opNoteEntries = Object.values(groupedOpNotes).map(opNote => ({
    solutionId: '6',
    icon: 'op_note',
    title: 'Op Note',
    affiliatedOrganization: opNote.affiliation,
    clinicOrganization: opNote.clinicOrganization,
    data: opNote.licenses
  }))

  const otherSolutions = sortedSolutions.filter(
    obj => obj.title !== 'AI Note' && obj.title !== 'Op Note'
  )

  const shouldIncludeAINote =
    (isDynamicLicensing &&
      combinedAINoteObject.data?.some(item => !!item.id)) ||
    combinedAINoteObject.data?.some(item => item.available > 0)

  return [
    ...otherSolutions,
    ...opNoteEntries,
    ...(shouldIncludeAINote ? [combinedAINoteObject] : [])
  ]
}

export const formatSolutionsListForUserForm = (
  sortedSolutions,
  licenses,
  isDynamicLicensing
) =>
  formatSolutionsList({
    sortedSolutions,
    licensesOrTransactions: licenses,
    isUserForm: true,
    isDynamicLicensing
  })

export const formatSolutionsListForLicensingPage = (
  sortedSolutions,
  licenseTransactions,
  isDynamicLicensing
) =>
  formatSolutionsList({
    sortedSolutions,
    licensesOrTransactions: licenseTransactions,
    isUserForm: false,
    isDynamicLicensing
  })

export const formatLicenseTransactionsData = (
  licenseTransactionsData,
  solutionList,
  userLicenses,
  isDynamicLicensing
) => {
  const formattedSolutions = sortSolutionsByType(solutionList).map(
    solution => ({
      solutionId: solution.id,
      icon: snakeCase(solution.type),
      title: startCase(solution.type),
      data: formatLicenseTransactionBySolution(
        licenseTransactionsData.filter(
          licenseTransaction => licenseTransaction?.solution?.id === solution.id
        )
      )
    })
  )

  return userLicenses
    ? formatSolutionsListForUserForm(
      formattedSolutions,
      userLicenses,
      isDynamicLicensing
    )
    : formatSolutionsListForLicensingPage(
      formattedSolutions,
      licenseTransactionsData,
      isDynamicLicensing
    )
}

function useLicenseTransactionCategories() {
  const canViewLicenseTransactionCategories = usePermissions(
    permissions.VIEW_LICENSE_TRANSACTION_CATEGORIES
  )

  const { data: { licenseTransactionCategories = {} } = {} } = useQuery(
    LICENSE_TRANSACTION_CATEGORIES_QUERY,
    {
      skip: !canViewLicenseTransactionCategories
    }
  )

  return licenseTransactionCategories.categories
}

export function useSelectLicenseTransactionCategories(solution) {
  let categories = useLicenseTransactionCategories()

  if (categories) {
    // eslint-disable-next-line no-unused-expressions
    solution === 'On Call Calendar'
      ? (categories = categories.filter(
        category => category.value === 'facility'
      ))
      : (categories = categories.filter(
        category => category.value !== 'facility'
      ))
  }

  return [categories]
}

export function sanitizeLicenseDataForUserForm(
  licenseTransactions,
  solutions,
  userLicenses,
  isDynamicLicensing
) {
  const formattedLicenseTransactionsData = formatLicenseTransactionsData(
    licenseTransactions,
    solutions,
    userLicenses,
    isDynamicLicensing
  ).filter(lt => lt.data.length)

  let sanitizedLicenseTransactions = formattedLicenseTransactionsData.map(
    licenseTransaction => {
      if (licenseTransaction.title === 'AI Note') {
        return {
          ...licenseTransaction,
          data: licenseTransaction.data.reduce((acc, licenseType) => {
            acc[licenseType.category] =
              (acc[licenseType.category] || 0) + (licenseType.available || 0)
            return acc
          }, {})
        }
      }

      return {
        ...licenseTransaction,
        data: licenseTransaction.data.reduce(
          (acc, licenseType) => ({
            ...acc,
            [licenseType.category]: licenseType.available || 0
          }),
          {}
        )
      }
    }
  )

  if (!userLicenses) return sanitizedLicenseTransactions

  const sanitizedLicenses = userLicenses.map(license => ({
    ...license,
    solutionId: license.licenseTransaction.solution.id
  }))

  sanitizedLicenseTransactions = sanitizedLicenseTransactions.map(
    licenseTransaction => {
      const { solutionId, affiliatedOrganization } = licenseTransaction

      const license = sanitizedLicenses.find(sanitizedLicense => {
        const isMatchingSolution = sanitizedLicense.solutionId === solutionId

        const affiliatedOrgIdOnLicense =
          sanitizedLicense.licenseTransaction.affiliatedOrganization?.id || null

        const affiliatedOrgId = affiliatedOrganization?.id || null

        const isMatchingAffiliatedOrg =
          affiliatedOrgIdOnLicense === affiliatedOrgId

        return isMatchingSolution && isMatchingAffiliatedOrg
      })

      return license ? { ...licenseTransaction, license } : licenseTransaction
    }
  )

  return sanitizedLicenseTransactions
}

export const getLicenses = account => {
  const { licenses } = account

  return licenses.map(item => item?.licenseTransaction?.solution?.type)
}

export const LICENSE_TYPES = Object.freeze({
  AI_NOTE: 'AINote',
  VOICE: 'Voice',
  OP_NOTE: 'OpNote',
  ON_CALL: 'OnCall',
  ON_CALL_CALENDAR: 'OnCallCalendar',
  SURGERY_WORKFLOW: 'SurgeryWorkflow',
  INTEGRATION_HUB: 'IntegrationHub',
  ENTERPRISE: 'Enterprise'
})

export default {
  LICENSE_TRANSACTIONS_QUERY,
  REQUEST_LICENSE_MUTATION,
  CREATE_LICENSE_MUTATION,
  EXPIRE_LICENSE_MUTATION,
  formatLicenseTransactionsData,
  useSelectLicenseTransactionCategories
}
